import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MediaDevices, useMediaDevices } from '@features/video/hooks/useMediaDevices';
import { VideoLog } from '@features/video/lib/log';
import { NoiseSuppressor, RNNOISE_SUPPORTED } from '@features/video/lib/rnnoise';
import { VideoRoomClient } from '@features/video/lib/VideoRoomClient';
import { MicIssue, RoomStatus, VideoStreamMode } from '@features/video/types';

// 'ended' — комната закрыта сервером, 'error' — связь потеряна или вход не удался
export type VideoStreamPhase = 'idle' | 'setup' | RoomStatus | 'ended' | 'error';

// Билет готовый или тот, что еще нужно получить: ошибку получения показывает хук
export type TicketSource = string | (() => Promise<string>);

export type VideoStreamOptions = {
  // какая это камера участника: уходит судье в имени треков
  mode: VideoStreamMode;
  // контекст журнала: турнир и, на телефоне без входа, участник из билета
  clubId: string;
  tournamentId: string;
  userId?: string;
  // микрофон, камера и звук судей при входе (дополнительная камера молчит)
  micOn?: boolean;
  camOn?: boolean;
  speakerOn?: boolean;
};

export type VideoStream = {
  phase: VideoStreamPhase;
  // ошибка связи; ошибки устройств живут в devices
  error: string | null;
  // камера и микрофон: превью, списки, ошибки доступа
  devices: MediaDevices;
  stream: MediaStream | null;
  micOn: boolean;
  camOn: boolean;
  speakerOn: boolean;
  // микрофон сломался в эфире после прерывания; fixMic — клик по кнопке на плашке
  micIssue: MicIssue;
  fixMic: () => void;
  // браузер не дал играть звук судей без клика; fixMic включает и его
  audioBlocked: boolean;
  // трек для индикатора уровня в эфире: выход шумодава, мьют его не касается; null — индикатор
  // берет захват
  meterTrack: MediaStreamTrack | null;
  // запросить камеру и микрофон для превью и выбора устройств
  openSetup: () => void;
  // отпустить устройства без подключения
  closeSetup: () => void;
  // подключиться к комнате: устройства запросит сам, если их еще нет; билет не дали —
  // ошибка в настройке
  join: (ticket: TicketSource) => void;
  // перейти в другую комнату, не отпуская устройства (приватная комната); билет не дали
  // или комната не пустила — остаемся, где были
  switchRoom: (ticket: TicketSource) => void;
  toggleMic: () => void;
  toggleCam: () => void;
  toggleSpeaker: () => void;
  // выйти из комнаты и отпустить устройства
  hangUp: () => void;
};

// Одно устройство участника: превью и выбор устройств, вход в комнату, кнопки
export function useVideoStream({
  mode,
  clubId,
  tournamentId,
  userId,
  micOn: initialMicOn = true,
  camOn: initialCamOn = true,
  speakerOn: initialSpeakerOn = true,
}: VideoStreamOptions): VideoStream {
  const [phase, setPhase] = useState<VideoStreamPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [micOn, setMicOn] = useState(initialMicOn);
  const [camOn, setCamOn] = useState(initialCamOn);
  const [speakerOn, setSpeakerOn] = useState(initialSpeakerOn);
  const [micIssue, setMicIssue] = useState<MicIssue>(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [meterTrack, setMeterTrack] = useState<MediaStreamTrack | null>(null);

  const refClient = useRef<VideoRoomClient | null>(null);
  const log = useMemo(
    () => new VideoLog({ role: 'player', mode, clubId, tournamentId, userId }),
    [mode, clubId, tournamentId, userId],
  );
  // билет, который ждет устройств: вход начинается, когда есть и то и другое
  const [pendingTicket, setPendingTicket] = useState<string | null>(null);

  // Устройства нужны от настройки до выхода из комнаты
  const devices = useMediaDevices({
    video: true,
    audio: true,
    enabled: phase !== 'idle' && phase !== 'ended',
  });
  const { stream, status } = devices;

  // Шумодав живет с настройки до входа, дальше им владеет клиент. Контекст рождается заранее,
  // до захвата и без клика, клик входа его только будит: так идет автовход после
  // перезагрузки, который работает везде, а контекст, созданный в клике при живом захвате,
  // в Firefox на Android не запускался (решение 31). Вход сорвался — шумодав остается
  // до следующей попытки
  const refSuppressor = useRef<NoiseSuppressor | null>(null);
  const dropSuppressor = useCallback(() => {
    refSuppressor.current?.destroy();
    refSuppressor.current = null;
  }, []);
  useEffect(() => {
    if (phase === 'idle' || phase === 'ended') {
      dropSuppressor();
      return;
    }
    if (refSuppressor.current || refClient.current || !RNNOISE_SUPPORTED) return;
    const suppressor = new NoiseSuppressor();
    refSuppressor.current = suppressor;
    suppressor.wake();
    // тяжелый WASM грузим, пока пользователь выбирает устройства
    suppressor.load().catch((e: unknown) => log.error('rnnoise_load_failed', e));
  }, [phase, dropSuppressor, log]);

  const disconnect = useCallback(() => {
    refClient.current?.disconnect();
    refClient.current = null;
    dropSuppressor();
    setMicIssue(null);
    setAudioBlocked(false);
    setMeterTrack(null);
  }, [dropSuppressor]);

  const start = useCallback(
    (ticket: string, media: MediaStream) => {
      const client = new VideoRoomClient({
        mode,
        log,
        micOn,
        camOn,
        speakerOn,
        rnnoise: refSuppressor.current,
      });
      refSuppressor.current = null;
      // вход без клика (автоподключение) дал suspended-контекст: захват уже есть, Chrome
      // после него дает запуститься; где нет — плашка попросит клик
      client.wake();
      refClient.current = client;
      refPublished.current = media;
      const unsubscribe = client.events.subscribe((e) => {
        switch (e.type) {
          case 'status':
            setPhase(e.status);
            break;
          case 'mic':
            setMicOn(e.on);
            break;
          case 'cam':
            setCamOn(e.on);
            break;
          case 'mic-issue':
            setMicIssue(e.issue);
            // контекст не запускается и в жесте: беда в захвате — закрываем контекст и берем
            // захват заново, клика для этого не нужно
            if (e.issue === 'stuck') {
              client.resetMic();
              refRetry.current();
            }
            break;
          case 'audio-blocked':
            setAudioBlocked(e.blocked);
            break;
          case 'meter':
            setMeterTrack(e.track);
            break;
          case 'disconnected':
            unsubscribe();
            refClient.current = null;
            client.disconnect();
            setMicIssue(null);
            setAudioBlocked(false);
            setMeterTrack(null);
            if (e.reason === 'ended') {
              setPhase('ended');
            } else {
              setError('Связь с комнатой потеряна');
              setPhase('error');
            }
            break;
        }
      });
      client.connect(ticket, media).catch((e: unknown) => {
        unsubscribe();
        refClient.current = null;
        client.disconnect();
        setMeterTrack(null);
        setError(e instanceof Error && e.message ? e.message : 'Не удалось подключиться');
        setPhase('error');
      });
    },
    [mode, log, micOn, camOn, speakerOn],
  );

  // Хук устройств дал новый поток (смена устройства, возврат захвата) — подменяем треки
  // в публикации, не выходя из комнаты; поток входа запоминает start
  const refPublished = useRef<MediaStream | null>(null);
  useEffect(() => {
    const client = refClient.current;
    if (!client || !stream || stream === refPublished.current) return;
    refPublished.current = stream;
    client.replaceTracks(stream).catch(() => {});
  }, [stream]);

  // Беды с устройствами: окружение игрока, в журнал событием; неизвестная — ошибкой
  const { camError, micError, error: mediaError } = devices;
  useEffect(() => {
    if (camError) log.event('device_error', { device: 'camera', kind: camError });
    if (micError) log.event('device_error', { device: 'microphone', kind: micError });
    if (mediaError?.kind === 'unknown') log.error('device_error', new Error(mediaError.message));
  }, [camError, micError, mediaError, log]);

  const join = useCallback((source: TicketSource) => {
    if (refClient.current) return;
    setError(null);
    setPhase('connecting');
    // будим контекст шумодава, пока жив клик; обычно он уже создан эффектом настройки
    refSuppressor.current ??= RNNOISE_SUPPORTED ? new NoiseSuppressor() : null;
    refSuppressor.current?.wake();
    resolveTicket(source).then(setPendingTicket, (e: unknown) => {
      // билет не дали: остаемся в настройке, ошибку показывает она
      setError(errorMessage(e, 'Не удалось получить доступ к комнате.'));
      setPhase('setup');
    });
  }, []);

  // Билет есть, устройства подоспели — подключаемся; устройств не дали — остаемся
  // в настройке, ошибки устройств покажет их выбор
  useEffect(() => {
    if (!pendingTicket || phase !== 'connecting') return;
    if (status === 'ready' && stream) {
      setPendingTicket(null);
      start(pendingTicket, stream);
    } else if (status === 'error') {
      setPendingTicket(null);
      setPhase('setup');
    }
  }, [phase, status, stream, start, pendingTicket]);

  const switchRoom = useCallback((source: TicketSource) => {
    const client = refClient.current;
    if (!client) return;
    setError(null);
    resolveTicket(source)
      .then((ticket) => {
        // пока ждали билет, связь могла оборваться: тот клиент уже отпущен
        if (refClient.current === client) return client.switchRoom(ticket);
      })
      .catch((e: unknown) => {
        // билет не дали — живое подключение не трогаем; не вошли ни в новую, ни в прежнюю
        // комнату — для участника это потеря связи
        if (client.isConnected) return;
        refClient.current = null;
        client.disconnect();
        setError(errorMessage(e, 'Не удалось подключиться'));
        setPhase('error');
      });
  }, []);

  const hangUp = useCallback(() => {
    setPendingTicket(null);
    disconnect();
    setError(null);
    setPhase('idle');
  }, [disconnect]);

  // Клик по «Включить микрофон»: захват отобрали — перезапрашиваем устройства, новый поток
  // подменит треки; контекст не запускается ('stuck') — закрываем его и тоже перезапрашиваем;
  // контекст шумодава уснул — будим его прямо в жесте
  const refRetry = useRef(devices.retry);
  refRetry.current = devices.retry;
  const fixMic = useCallback(() => {
    const client = refClient.current;
    if (micIssue === 'lost' || micIssue === 'stuck') {
      if (micIssue === 'stuck') client?.resetMic();
      devices.retry();
    } else {
      client?.wakeMic();
    }
    client?.startAudio();
  }, [micIssue, devices]);

  const toggleMic = useCallback(() => {
    const next = !micOn;
    setMicOn(next);
    refClient.current?.setMicOn(next).catch(() => {});
  }, [micOn]);

  const toggleCam = useCallback(() => {
    const next = !camOn;
    setCamOn(next);
    refClient.current?.setCamOn(next).catch(() => {});
  }, [camOn]);

  const toggleSpeaker = useCallback(() => {
    const next = !speakerOn;
    setSpeakerOn(next);
    refClient.current?.setSpeakerOn(next);
  }, [speakerOn]);

  // Уход со страницы: выходим из комнаты; устройства отпустит их хук
  useEffect(() => disconnect, [disconnect]);

  return useMemo(
    () => ({
      phase,
      error,
      devices,
      stream,
      micOn,
      camOn,
      speakerOn,
      // плашка связи важнее: пока она восстанавливается, беду с микрофоном не показываем
      micIssue: phase === 'reconnecting' ? null : micIssue,
      fixMic,
      audioBlocked,
      meterTrack,
      openSetup: () => {
        setError(null);
        setPhase('setup');
      },
      closeSetup: hangUp,
      join,
      switchRoom,
      toggleMic,
      toggleCam,
      toggleSpeaker,
      hangUp,
    }),
    [
      phase,
      error,
      devices,
      stream,
      micOn,
      camOn,
      speakerOn,
      micIssue,
      fixMic,
      audioBlocked,
      meterTrack,
      join,
      switchRoom,
      toggleMic,
      toggleCam,
      toggleSpeaker,
      hangUp,
    ],
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

function resolveTicket(source: TicketSource): Promise<string> {
  return typeof source === 'function' ? source() : Promise.resolve(source);
}

// Свои ошибки (сервер связи, билет) — как есть, ошибки API — общим текстом
function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message && !/^[A-Za-z]/.test(e.message) ? e.message : fallback;
}
